import React, { useState, useEffect } from "react";
import { useTranslations } from "use-intl";
import { Calendar } from "@/components/ui/calendar";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { format } from "date-fns";
import ActualSekAmountField from "@/components/shared/ActualSekAmountField";
import { parseActualSekAmount } from "@/components/shared/actualSekAmount";
import { Receipt } from "@/types/receipt";
import { BankAccountsStatus, TransactionSuggestion } from "@/types/payments";
import { ServiceRegistry } from "@/services/ServiceRegistry";
import { fetchWithPrecomputedFallback, settleSuggestionFetch } from "@/utils/matchSuggestionFallback";
import { useZeroMatchAcknowledgment } from "@/hooks/useZeroMatchAcknowledgment";
import { useMatchModalViewState, useMatchModalTitle } from "@/hooks/useMatchModalViewState";
import { useCorrectionBanner } from "@/i18n/useMatching";
import { useFormatPickedDate } from "@/i18n/useFormatPickedDate";
import MatchModalBody from "@/components/shared/MatchModalBody";

interface MarkAsMatchedModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  receipt: Receipt | null;
  /**
   * See PayableMatchSuggestionsModal's `hasBankAccounts` docstring for the full contract.
   * `'loading'` / `'error'` (#498 whole-branch review finding I2) must never be treated as
   * `false` — that would silently route a receipt whose accounts are genuinely still connected
   * (or were, until a bank consent expired) through the trusted, unaudited case-a write.
   */
  hasBankAccounts: BankAccountsStatus;
  onMatchTransaction: (receiptId: string, transaction: TransactionSuggestion) => void;
  onMarkMatchedExternally: (receiptId: string, formattedDate: string, actualSekAmount?: number) => void;
  /**
   * Case c (issue #498, Task 5) — a bank account is connected, but the search found no
   * candidates, or the consultant clicked "None of these match" on a non-empty result. Routes to
   * ReceiptService.acknowledgeExternalMatch (an audited write, distinct from
   * onMarkMatchedExternally's trusted case-a write with no audit row). Unlike receivables (Task
   * 4), receipts' backend action accepts an actualSekAmount, matching payables (Task 3) — see
   * supabase/functions/receipt-api/index.ts's 'acknowledgeExternalMatch' case.
   */
  onAcknowledgeExternalMatch?: (receiptId: string, formattedDate: string, actualSekAmount?: number) => void;
  formatCurrency: (amount: number, currency: string) => string;
}

/**
 * Issue #457: try the precomputed auto_match_decisions suggestion first (already computed by the
 * async match_jobs pipeline); fall back to the live JIT scoring query only when that's empty —
 * e.g. nothing has been scored yet for this document. `forceRefresh` (the explicit "Search
 * again" action) always calls the JIT path directly, regardless of what's precomputed, so a
 * transaction too new for the ~1-minute match-job cron to have scored yet can still be found.
 */
function fetchSuggestions(
  financialDocumentId: string,
  forceRefresh: boolean,
): Promise<TransactionSuggestion[]> {
  const service = ServiceRegistry.getReceiptService();
  return fetchWithPrecomputedFallback(
    () => service.getPrecomputedTransactionSuggestions(financialDocumentId),
    () => service.getTransactionSuggestions(financialDocumentId),
    forceRefresh,
  );
}

/**
 * Four states when `hasBankAccounts` is true (mirrors PayableMatchSuggestionsModal's and
 * MarkAsReceivedModal's now-established pattern from issue #498 Tasks 3/4 — see those
 * components' docstrings for the fuller rationale; the notes below focus on what's specific to
 * receipts):
 *
 *  - still checking (fetch in flight) — a distinct loading state; the acknowledgment checkbox
 *    must never render before the fetch resolves (design spec AC7).
 *  - loadFailed — a distinct "couldn't check for matches, try again" retry state. Before this
 *    task, a rejected fetchSuggestions() call landed in the same `.catch` that also ran for a
 *    genuinely empty result (`setSuggestions([])` either way, no separate flag) — a failed search
 *    and a confirmed-empty one were visually and functionally identical. Verified by reading the
 *    pre-fix component before writing the fix — same bug class PayableMatchSuggestionsModal
 *    already fixed for issue #462's finding 3, and MarkAsReceivedModal fixed for Task 4 (design
 *    spec AC8, and `.claude/rules/client-error-handling.md`'s Tier 2 pattern, applied here even
 *    though this component lives in `src/`, not `apps/client/`).
 *  - candidates found — the existing suggestion table (case b, unchanged), plus a "None of these
 *    match" escape hatch (AC6) that transitions into the same acknowledgment flow used for zero
 *    candidates.
 *  - zero candidates (or "None of these match" was clicked) — plain-language text plus a single
 *    fixed-copy checkbox (ACKNOWLEDGMENT_COPY above) that must be ticked before the date-picker
 *    Confirm control is enabled (AC4/AC5). Unlike receivables (Task 4), the foreign-currency
 *    settled-amount field IS shared with case a here (AC9) — receipts' backend
 *    `acknowledgeExternalMatch` action accepts `actualSekAmount`, matching payables (Task 3), not
 *    receivables' `acknowledgeExternalPayment` (which only accepts receivableId+receivedDate).
 *    Before this task, this branch (reached whenever `hasBankAccounts=true` and the search
 *    completed empty, or "None of these match"/`showExternalForm` was used) rendered a plain
 *    date-picker with the trusted `onMarkMatchedExternally` write and NO checkbox at all — a
 *    bank-account-connected zero-match receipt was written with the same no-audit-trail call as
 *    the no-bank-accounts case. That gap is what this task closes.
 */
const MarkAsMatchedModal: React.FC<MarkAsMatchedModalProps> = ({
  open,
  setOpen,
  receipt,
  hasBankAccounts,
  onMatchTransaction,
  onMarkMatchedExternally,
  onAcknowledgeExternalMatch,
  formatCurrency,
}) => {
  const t = useTranslations("consultantMatching.receipt");
  const shared = useTranslations("consultantMatching.shared");
  const correction = useTranslations("consultantMatching.correction");
  const buildCorrectionBanner = useCorrectionBanner("receipt");
  const formatPickedDate = useFormatPickedDate();
  const getMatchModalTitle = useMatchModalTitle();
  // Boilerplate state every "mark as X" modal in this family opens with — reported as a clone
  // against receivables' MarkAsReceivedModal.tsx; see that file's identical comment.
  /* jscpd:ignore-start */
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [suggestions, setSuggestions] = useState<TransactionSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState<TransactionSuggestion | null>(null);
  /* jscpd:ignore-end */
  const [actualSekAmountInput, setActualSekAmountInput] = useState("");
  // Shared with PayableMatchSuggestionsModal and receivables' MarkAsReceivedModal (issue #498)
  // — see the hook's own docstring.
  /* jscpd:ignore-start — identical hook-call destructure is the point: all three callers share
   * the exact same useZeroMatchAcknowledgment contract by design (issue #498's "Consistency
   * across document types" requirement). Not further extractable without merging components. */
  const {
    loadFailed, setLoadFailed, noneOfTheseMatch, acknowledged, setAcknowledged,
    reset: resetAcknowledgment, onNoneOfTheseMatch: handleNoneOfTheseMatch, onBackToSuggestions: handleBackToSuggestions,
  } = useZeroMatchAcknowledgment(() => setSelectedSuggestion(null));
  /* jscpd:ignore-end */

  const loadSuggestions = (financialDocumentId: string, forceRefresh: boolean) => {
    setLoading(true);
    setSelectedSuggestion(null);
    resetAcknowledgment();
    settleSuggestionFetch(
      fetchSuggestions(financialDocumentId, forceRefresh),
      { setSuggestions, setLoadFailed, setLoading },
      "Failed to fetch transaction suggestions:",
    );
  };

  useEffect(() => {
    if (open && receipt) {
      // Prefill with the receipt's current SEK estimate — confirming the default
      // is a single click; the user only edits it when the real amount differed.
      setActualSekAmountInput(receipt.sek_amount != null ? String(receipt.sek_amount) : "");
      // #498 Task 8 — reopening an already-matched receipt shows the PREVIOUSLY entered match
      // date instead of defaulting to today (design spec Flow 7 step 2). A first-time
      // mark-as-matched has no matched_at yet, so this falls through to `new Date()`, unchanged
      // from before this task.
      setSelectedDate(receipt.matched_at ? new Date(receipt.matched_at) : new Date());
      if (hasBankAccounts === true) {
        loadSuggestions(receipt.financial_document_id, false);
      } else {
        // Case a (hasBankAccounts === false) or bank-status-unknown (#498 finding I2 —
        // hasBankAccounts is 'loading'/'error'): skip the fetch entirely, no matching language.
        // There is nothing to search yet while the system doesn't know if accounts are connected.
        setSuggestions([]);
        setLoadFailed(false);
        setLoading(false);
      }
    }
    if (!open) {
      setSuggestions([]);
      setSelectedSuggestion(null);
      setSelectedDate(new Date());
      resetAcknowledgment();
      setActualSekAmountInput("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, receipt, hasBankAccounts]);

  const handleSearchAgain = () => {
    if (!receipt) return;
    loadSuggestions(receipt.financial_document_id, true);
  };

  const hasSuggestions = suggestions.length > 0;
  // Shared with PayableMatchSuggestionsModal and receivables' MarkAsReceivedModal (issue #498)
  // — see the hook's own docstring for what each of the five states means. Called before the
  // `!receipt` early return below — React Hooks must run unconditionally on every render, and
  // `receipt` can transition from non-null to null while this modal stays open.
  const { showCandidateTable, showStillChecking, showLoadFailed, showBankStatusUnknown } =
    useMatchModalViewState({ hasBankAccounts, loading, loadFailed, hasSuggestions, noneOfTheseMatch });

  if (!receipt) return null;

  const isForeignCurrency = receipt.currency !== "SEK";
  const { amount: parsedActualSekAmount } = parseActualSekAmount(actualSekAmountInput);
  const isActualSekAmountValid = parsedActualSekAmount !== null;

  const handleConfirmMatch = () => {
    if (selectedSuggestion) {
      onMatchTransaction(receipt.id, selectedSuggestion);
      setOpen(false);
    }
  };

  // Case a: no bank accounts connected — plain "matched externally" write, unchanged path.
  // `onMarkMatchedExternally` is fire-and-forget (ReceiptsPage.handleMarkMatchedExternally owns
  // its own try/catch, toast, and refresh) — this handler correctly reflects that: no loading
  // state is faked around it, it just fires the write and closes (see Task 3's fix-round-1 report
  // for why a state toggle around a synchronous/fire-and-forget call is dead code).
  const handleConfirmExternalPayment = () => {
    if (isForeignCurrency && !isActualSekAmountValid) {
      return;
    }
    const formattedDate = format(selectedDate, "yyyy-MM-dd");
    onMarkMatchedExternally(receipt.id, formattedDate, isForeignCurrency ? parsedActualSekAmount : undefined);
    setOpen(false);
  };

  // Case c: zero-match / "None of these match" acknowledgment write (issue #498, Task 5).
  // `onAcknowledgeExternalMatch` is fire-and-forget for the same reason as
  // handleConfirmExternalPayment above — mirrors PayableMatchSuggestionsModal's
  // handleConfirmAcknowledgment exactly.
  const handleConfirmAcknowledgment = () => {
    if (!acknowledged) return;
    if (isForeignCurrency && !isActualSekAmountValid) {
      return;
    }
    if (onAcknowledgeExternalMatch) {
      const formattedDate = format(selectedDate, "yyyy-MM-dd");
      onAcknowledgeExternalMatch(receipt.id, formattedDate, isForeignCurrency ? parsedActualSekAmount : undefined);
    }
    setOpen(false);
  };

  // #498 Task 8 — null whenever this is a first-time mark-as-matched (receipt.status not yet
  // 'matched'); see buildCorrectionBanner's docstring.
  const correctionBanner = receipt.status === "matched" ? buildCorrectionBanner(receipt.paymentVerification) : null;
  // AC9 — the foreign-currency settled-amount field is shared verbatim between case a (no
  // accounts connected) and case c (zero-match acknowledgment), not duplicated.
  const renderPaymentDetailsFields = () => (
    <>
      <div className="flex justify-center">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={(date) => date && setSelectedDate(date)}
          autoFocus
          className="rounded-md border shadow p-3 pointer-events-auto"
        />
      </div>
      <div className="text-sm text-center">
        {shared("selectedDate")} <span className="font-medium">{formatPickedDate(selectedDate)}</span>
      </div>
      {isForeignCurrency && (
        <ActualSekAmountField
          id="receipt-actual-sek-amount"
          value={actualSekAmountInput}
          onChange={setActualSekAmountInput}
        />
      )}
    </>
  );

  const title = getMatchModalTitle(
    { showBankStatusUnknown, showLoadFailed, showCandidateTable, showStillChecking },
    hasBankAccounts,
    t("caseA"),
  );

  /* jscpd:ignore-start — the MatchModalBody call site (shell props, forwarded local state, and
   * the component's own closing boilerplate) is identical in shape to payables' and
   * receivables' call sites below the same props. Not further extractable without merging
   * payables/receivables/receipts into one generic component, which is a separate, larger,
   * higher-risk change (touches each page's own onPay/onMatchTransaction/
   * onMarkMatchedExternally call signatures) deliberately kept out of this PR's scope. */
  return (
    <MatchModalBody
      open={open}
      setOpen={setOpen}
      title={title}
      subtitle={<>{receipt.vendor ?? receipt.description ?? t("fallbackName")} — {formatCurrency(receipt.amount, receipt.currency)}</>}
      correctionBanner={correctionBanner && (
        <Alert>
          <AlertTitle>{correction("title")}</AlertTitle>
          <AlertDescription>{correctionBanner}</AlertDescription>
        </Alert>
      )}
      hasBankAccounts={hasBankAccounts}
      showBankStatusUnknown={showBankStatusUnknown}
      showStillChecking={showStillChecking}
      showLoadFailed={showLoadFailed}
      showCandidateTable={showCandidateTable}
      onRetry={handleSearchAgain}
      caseAContent={
        <>
          <div className="text-sm text-muted-foreground">{t("note")}</div>
          {renderPaymentDetailsFields()}
        </>
      }
      onConfirmCaseA={handleConfirmExternalPayment}
      caseAConfirmLabel={t("confirm")}
      caseAConfirmDisabled={isForeignCurrency && !isActualSekAmountValid}
      suggestions={suggestions}
      selectedSuggestionId={selectedSuggestion?.transactionId ?? null}
      onSelectSuggestion={setSelectedSuggestion}
      formatCurrency={formatCurrency}
      onSearchAgain={handleSearchAgain}
      onNoneOfTheseMatch={handleNoneOfTheseMatch}
      onConfirmMatch={handleConfirmMatch}
      confirmMatchDisabled={!selectedSuggestion}
      acknowledgeCheckboxId="acknowledge-unmatched-receipt"
      acknowledged={acknowledged}
      onAcknowledgedChange={setAcknowledged}
      caseCContent={renderPaymentDetailsFields()}
      noneOfTheseMatch={noneOfTheseMatch}
      onBackToSuggestions={handleBackToSuggestions}
      onConfirmAcknowledgment={handleConfirmAcknowledgment}
      confirmAcknowledgmentDisabled={!acknowledged || (isForeignCurrency && !isActualSekAmountValid)}
    />
  );
};
/* jscpd:ignore-end */

export default MarkAsMatchedModal;
